import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    inviteUuid: String,
    bookId: Number,
  }

  declare inviteUuidValue: string
  declare bookIdValue: number

  private selectedStatements: Set<number> = new Set()
  private isSaving: boolean = false
  private saveQueued: boolean = false
  private queuedSilent: boolean = true

  connect() {
    this.loadExistingResponses()
    document.addEventListener(
      "participant:saveSelectionsBeforeSubmit",
      this.saveBeforeSubmitHandler,
    )
  }

  disconnect() {
    document.removeEventListener(
      "participant:saveSelectionsBeforeSubmit",
      this.saveBeforeSubmitHandler,
    )
  }

  private saveBeforeSubmitHandler = (event: Event) => {
    const customEvent = event as CustomEvent<{ silent?: boolean }>
    this.saveAllStatements(customEvent.detail?.silent ?? false)
  }

  toggleStatement(event: Event) {
    const target = event.currentTarget as HTMLElement
    const statementId = parseInt(target.dataset.statementId || "0", 10)

    if (!statementId) {
      console.error("Invalid statement data", { statementId })
      return
    }

    if (this.selectedStatements.has(statementId)) {
      this.selectedStatements.delete(statementId)
    } else {
      this.selectedStatements.add(statementId)
    }

    this.updateStatementButton(statementId)
    this.saveAllStatements(true)
  }

  confirmSelection() {
    this.saveAllStatements(false)
  }

  private loadExistingResponses() {
    const statementButtons = this.element.querySelectorAll("[data-statement-id]")
    const selectedColor = "rgb(237, 244, 242)"

    statementButtons.forEach((button) => {
      const statementId = parseInt((button as HTMLElement).dataset.statementId || "0", 10)
      if (statementId) {
        const bgColor = window.getComputedStyle(button as HTMLElement).backgroundColor
        const isSelected =
          bgColor === selectedColor ||
          (bgColor.toLowerCase().includes("237") &&
            bgColor.includes("244") &&
            bgColor.includes("242"))

        if (isSelected) {
          this.selectedStatements.add(statementId)
        }
      }
    })
  }

  private updateStatementButton(statementId: number) {
    const button = this.element.querySelector(`[data-statement-id="${statementId}"]`) as HTMLElement
    if (!button) return

    const selectedColor = "#EDF4F2"
    const unselectedColor = "#EEECF6"
    const borderRadius = "0.5rem"

    if (this.selectedStatements.has(statementId)) {
      button.style.backgroundColor = selectedColor
    } else {
      button.style.backgroundColor = unselectedColor
    }
    button.style.borderRadius = borderRadius
  }

  private getAllStatementIds(): number[] {
    const statementButtons = this.element.querySelectorAll("[data-statement-id]")
    return Array.from(statementButtons)
      .map((button) => {
        return parseInt((button as HTMLElement).dataset.statementId || "0", 10)
      })
      .filter((id) => id > 0)
  }

  private async saveAllStatements(silent: boolean = false) {
    if (this.isSaving) {
      this.saveQueued = true
      this.queuedSilent = this.queuedSilent && silent
      return
    }

    if (this.selectedStatements.size === 0 && !silent) {
      this.showFlash("Please select at least one statement.", "danger")
      return
    }

    this.isSaving = true

    try {
      const formData = new FormData()
      formData.append("invite_uuid", this.inviteUuidValue)
      formData.append("book_id", this.bookIdValue.toString())
      formData.append("authenticity_token", this.getAuthenticityToken())

      // Tell the server which statements belong to this slide so it only
      // adds/removes responses within this subset.
      this.getAllStatementIds().forEach((statementId) => {
        formData.append("visible_statement_ids[]", statementId.toString())
      })

      this.selectedStatements.forEach((statementId) => {
        formData.append("statement_ids[]", statementId.toString())
      })

      const response = await fetch("/participant/save_statement_voting_response", {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.getAuthenticityToken(),
          Accept: "application/json",
        },
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Failed to save responses: ${response.statusText}`)
      }

      const result = await response.json()
      if (!silent) {
        if (result.status === "ok") {
          this.showFlash("Your selections have been saved!", "success")
        } else {
          console.error("Error saving responses:", result.error)
          this.showFlash("There was an error saving your selections. Please try again.", "danger")
        }
      }
    } catch (error) {
      console.error("Error saving responses:", error)
      if (!silent) {
        this.showFlash("There was an error saving your selections. Please try again.", "danger")
      }
    } finally {
      this.isSaving = false
      if (this.saveQueued) {
        this.saveQueued = false
        const queuedSilent = this.queuedSilent
        this.queuedSilent = true
        this.saveAllStatements(queuedSilent)
      }
    }
  }

  private getAuthenticityToken(): string {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    if (!token) {
      throw new Error("CSRF token not found")
    }
    return token
  }

  private showFlash(message: string, variant: "success" | "danger" = "success") {
    const main = document.querySelector("main#main")
    if (!main) return

    const existingAlerts = main.querySelectorAll(".alert[data-turbo-temporary]")
    existingAlerts.forEach((alert) => {
      alert.remove()
    })

    const alert = document.createElement("div")
    alert.className = `mb-org3 alert alert-${variant} alert-dismissible fade show zIndex1`
    alert.setAttribute("role", "alert")
    alert.setAttribute("data-turbo-temporary", "true")
    alert.innerHTML = `
      ${this.escapeHtml(message)}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `

    main.insertBefore(alert, main.firstChild)

    setTimeout(() => {
      if (alert.parentNode) {
        alert.classList.remove("show")
        setTimeout(() => alert.remove(), 150)
      }
    }, 3000)
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
